import { deleteCookie, getCookie, setCookie } from 'cookies-next'

/**
 * Cookie consent for TBL pages, kept in a first-party cookie for a year. Until the
 * visitor chooses, Google runs in Consent Mode v2 with everything denied, and the Meta Pixel and
 * Hotjar don't load. The tbl_attr attribution cookie is first-party essential and isn't gated.
 */
export interface ConsentState {
  essential: true
  analytics: boolean
  marketing: boolean
  timestamp: string
  version: string
}

export const CONSENT_COOKIE = 'tbl_consent'
export const CONSENT_TTL_SECONDS = 60 * 60 * 24 * 365 // 365 days
// Bumping this asks everyone again: a saved choice from another version is ignored.
export const CONSENT_VERSION = '1.0'

export const buildConsent = (acceptAll: boolean, now: Date): ConsentState => ({
  essential: true,
  analytics: acceptAll,
  marketing: acceptAll,
  timestamp: now.toISOString(),
  version: CONSENT_VERSION,
})

export const parseConsent = (value: unknown): ConsentState | null => {
  if (typeof value !== 'string' || !value) return null
  try {
    const parsed = JSON.parse(value)
    if (!parsed || typeof parsed !== 'object' || parsed.version !== CONSENT_VERSION) return null
    return {
      essential: true,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      timestamp: String(parsed.timestamp ?? ''),
      version: CONSENT_VERSION,
    }
  } catch {
    return null
  }
}

export const getConsent = (): ConsentState | null => parseConsent(getCookie(CONSENT_COOKIE))

export const saveConsent = (consent: ConsentState) => {
  setCookie(CONSENT_COOKIE, JSON.stringify(consent), {
    maxAge: CONSENT_TTL_SECONDS,
    path: '/',
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
  })
}

// Cookies set by GA4 and Google Ads (_ga, _gid, _gat, _gcl_*), the Meta Pixel (_fbp, _fbc) and Hotjar (_hj*).
const TRACKING_COOKIE = /^(_ga|_gid|_gat|_gcl_|_fbp|_fbc|_hj)/

export const trackingCookieNames = (cookieHeader: string): string[] =>
  cookieHeader
    .split(';')
    .map((cookie) => cookie.split('=')[0].trim())
    .filter((name) => TRACKING_COOKIE.test(name))

export const cookieDomains = (hostname: string): (string | undefined)[] => {
  const labels = hostname.split('.')
  const parents = labels.slice(0, -1).map((_, i) => labels.slice(i).join('.'))
  return [undefined, ...parents]
}

/** Deletes the analytics and marketing cookies already set, when the visitor withdraws consent. */
export const clearTrackingCookies = () => {
  const domains = cookieDomains(window.location.hostname)
  trackingCookieNames(document.cookie).forEach((name) =>
    domains.forEach((domain) => deleteCookie(name, { path: '/', domain })),
  )
}

// Hotjar also keeps identifiers in web storage (_hjSessionUser_*, hjViewportId, hjActiveViewportIds).
const TRACKING_STORAGE_KEY = /^_?hj/

export const trackingStorageKeys = (keys: string[]): string[] =>
  keys.filter((key) => TRACKING_STORAGE_KEY.test(key))

/** Deletes the analytics identifiers kept in localStorage and sessionStorage, on withdrawal. */
export const clearTrackingStorage = () => {
  ;(['localStorage', 'sessionStorage'] as const).forEach((name) => {
    try {
      const storage = window[name]
      trackingStorageKeys(Object.keys(storage)).forEach((key) => storage.removeItem(key))
    } catch {
      // Storage can be blocked (private mode, site data disabled): nothing to clear then.
    }
  })
}

export const googleConsent = ({
  analytics,
  marketing,
}: Pick<ConsentState, 'analytics' | 'marketing'>) => {
  const ads = marketing ? 'granted' : 'denied'
  return {
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    analytics_storage: analytics ? 'granted' : 'denied',
  }
}

/**
 * Inline <head> script for TBL pages, run before gtag/GTM load: Consent Mode v2 defaults to denied,
 * then a choice saved on an earlier visit is applied at once so returning visitors are tracked from
 * the first hit. Reads the cookie itself because it runs before any app code.
 */
export const consentBootstrapScript = () => `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('consent', 'default', ${JSON.stringify(
  googleConsent({ analytics: false, marketing: false }),
)});
try {
  var match = document.cookie.match(/(?:^|; )${CONSENT_COOKIE}=([^;]*)/);
  var saved = match && JSON.parse(decodeURIComponent(match[1]));
  if (saved && saved.version === '${CONSENT_VERSION}') {
    var ads = saved.marketing === true ? 'granted' : 'denied';
    gtag('consent', 'update', {
      ad_storage: ads,
      ad_user_data: ads,
      ad_personalization: ads,
      analytics_storage: saved.analytics === true ? 'granted' : 'denied'
    });
  }
} catch (e) {}
`
